import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import {
  FieldError,
  FieldLabel,
  campInputClass,
} from '@/features/campaigns/components/FormPrimitives';

export type SelectOption = {
  key: string;
  label: string;
  searchText?: string;
  disabled?: boolean;
  disabledReason?: string;
  tag?: string;
  tagTone?: 'success' | 'warning' | 'muted';
};

type CommonSelectProps = {
  label: string;
  name: string;
  value: string;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  searchable?: boolean;
  required?: boolean;
  hint?: string;
  /** Tighter label spacing — no reserved hint row. */
  compact?: boolean;
  action?: ReactNode;
  footer?: ReactNode;
  onChange: (name: string, value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type MenuStyle = CSSProperties;

function tagClass(tone: SelectOption['tagTone']) {
  if (tone === 'success') return 'bg-[#ECFDF5] text-[#297857]';
  if (tone === 'warning') return 'bg-[#FFFBEB] text-[#B45309]';
  return 'bg-[#F4F7FA] text-[#7C8B88]';
}

function buildMenuStyle(trigger: HTMLElement): MenuStyle {
  const rect = trigger.getBoundingClientRect();
  const gap = 6;
  const pad = 12;
  const maxMenu = 320;
  const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
  const spaceAbove = rect.top - gap - pad;
  const openDown = spaceBelow >= 140 || spaceBelow >= spaceAbove;

  const base: MenuStyle = {
    position: 'fixed',
    left: Math.max(pad, Math.min(rect.left, window.innerWidth - rect.width - pad)),
    width: rect.width,
    zIndex: 500,
    maxHeight: Math.min(maxMenu, openDown ? Math.max(140, spaceBelow) : Math.max(140, spaceAbove)),
  };

  if (openDown) {
    return { ...base, top: rect.bottom + gap };
  }

  return {
    ...base,
    bottom: window.innerHeight - rect.top + gap,
  };
}

export function CommonSelect({
  label,
  name,
  value,
  options,
  placeholder = 'Select…',
  error,
  searchable = false,
  required = false,
  hint,
  compact = false,
  action,
  footer,
  onChange,
  open: openProp,
  onOpenChange,
}: CommonSelectProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<MenuStyle | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const isControlled = openProp !== undefined;
  const open = isControlled ? Boolean(openProp) : uncontrolledOpen;


  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const selected = options.find((o) => String(o.key) === String(value));

  const filtered = searchable
    ? options.filter((o) => {
        const hay = (o.searchText || o.label).toLowerCase();
        return hay.includes(query.toLowerCase());
      })
    : options;

  const syncMenuPosition = () => {
    if (!triggerRef.current) return;
    setMenuStyle(buildMenuStyle(triggerRef.current));
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    syncMenuPosition();
  }, [open, filtered.length, footer]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open || !searchable) return;
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const menu =
    open && menuStyle && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            style={menuStyle}
            className="flex flex-col overflow-hidden rounded-xl border border-[#E5EAF0] bg-white shadow-[0_16px_40px_-18px_rgba(16,38,73,0.45)]"
          >
            {searchable ? (
              <div className="shrink-0 border-b border-[#E5EAF0] p-1.5">
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="w-full rounded-lg border border-[#E5EAF0] bg-[#F4F7FA] px-3 py-2 text-sm outline-none focus:border-[#145E8C]"
                />
              </div>
            ) : null}
            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-sm text-[#7C8B88]">No options</p>
              ) : (
                filtered.map((opt) => {
                  const disabled = Boolean(opt.disabled);
                  const tip = disabled
                    ? opt.disabledReason ||
                      'Only verified clients can be selected'
                    : undefined;
                  return (
                    <div
                      key={opt.key}
                      title={tip}
                      className={disabled ? 'cursor-not-allowed' : undefined}
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={String(opt.key) === String(value)}
                        aria-disabled={disabled}
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          onChange(name, opt.key);
                          setOpen(false);
                        }}
                        className={`flex w-full items-start justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition ${
                          disabled
                            ? 'pointer-events-none text-[#7C8B88] opacity-60'
                            : opt.key === value
                              ? 'bg-[#EAF3F8] font-semibold text-[#145E8C]'
                              : 'text-[#102649] hover:bg-[#F4F7FA]'
                        }`}
                      >
                        <span className="min-w-0 break-words leading-snug">
                          {opt.label}
                        </span>
                        {opt.tag ? (
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ${tagClass(opt.tagTone)}`}
                          >
                            {opt.tag}
                          </span>
                        ) : null}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            {footer ? (
              <div className="shrink-0 border-t border-[#E5EAF0] bg-white p-1.5">
                {footer}
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`relative flex w-full flex-col text-left ${compact ? 'gap-1' : 'gap-1.5'}`}
    >
      {compact ? (
        <label className="text-[12px] font-medium tracking-wide text-[#5B6B7C]">
          {label}
          {required ? (
            <span className="ml-0.5 text-[#8E58A6]" aria-hidden>
              *
            </span>
          ) : null}
        </label>
      ) : (
        <FieldLabel required={required} hint={hint}>
          {label}
        </FieldLabel>
      )}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={Boolean(error)}
        onClick={() => setOpen(!open)}
        className={`${campInputClass} flex items-center justify-between gap-2 text-left ${
          compact ? 'h-10' : ''
        } ${error ? 'is-invalid' : ''} ${
          open ? 'border-[#145E8C] bg-white ring-2 ring-[#145E8C]/15' : ''
        }`}
      >
        <span
          className={`min-w-0 truncate ${
            selected ? 'text-[#102649]' : 'text-[#9AA8B8]'
          }`}
        >
          {selected?.label || placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {selected?.tag ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase ${tagClass(selected.tagTone)}`}
            >
              {selected.tag}
            </span>
          ) : null}
          <ChevronDown
            className={`h-4 w-4 text-[#7C8B88] transition ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {menu}
      {action ? <div className="pt-0.5">{action}</div> : null}
      <FieldError message={error} />
    </div>
  );
}
